import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Home, ArrowLeft, LogIn, AlertCircle } from 'lucide-react';

export const AppNotFound = () => {
  const navigate = useNavigate();
  const [hasSession, setHasSession] = useState<boolean | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setHasSession(!!session);
    });
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col justify-center items-center px-4 relative overflow-hidden">
      {/* Decorative background grid and gradients */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#0f172a_1px,transparent_1px),linear-gradient(to_bottom,#0f172a_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] opacity-60"></div>
      
      {/* Glow effects */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-indigo-500/10 rounded-full blur-[120px] pointer-events-none"></div>
      <div className="absolute top-1/3 left-1/3 w-[300px] h-[300px] bg-blue-500/10 rounded-full blur-[100px] pointer-events-none"></div>

      <div className="max-w-md w-full text-center space-y-8 relative z-10">
        {/* Main Icon and 404 text */}
        <div className="space-y-4">
          <div className="inline-flex p-4 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 animate-pulse">
            <AlertCircle className="w-12 h-12" />
          </div>
          <h1 className="text-8xl font-black tracking-tighter bg-gradient-to-b from-indigo-200 via-indigo-400 to-indigo-600 bg-clip-text text-transparent select-none">
            404
          </h1>
          <h2 className="text-2xl font-bold text-slate-100 tracking-tight">
            Página Não Encontrada
          </h2>
          <p className="text-slate-400 text-sm max-w-xs mx-auto leading-relaxed">
            A URL que você tentou acessar não existe ou foi movida no painel administrativo do ImobPanda.
          </p>
        </div>

        {/* Buttons / Actions */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
          {hasSession === true ? (
            <Button
              onClick={() => navigate('/dashboard')}
              className="bg-indigo-600 hover:bg-indigo-500 text-white font-medium px-6 py-2.5 rounded-xl transition-all duration-200 shadow-lg shadow-indigo-600/20 hover:shadow-indigo-600/30 flex items-center justify-center gap-2 group"
            >
              <Home className="w-4 h-4" />
              Ir para o Painel
            </Button>
          ) : hasSession === false ? (
            <Button
              onClick={() => navigate('/login')}
              className="bg-indigo-600 hover:bg-indigo-500 text-white font-medium px-6 py-2.5 rounded-xl transition-all duration-200 shadow-lg shadow-indigo-600/20 hover:shadow-indigo-600/30 flex items-center justify-center gap-2 group"
            >
              <LogIn className="w-4 h-4" />
              Acessar minha conta
            </Button>
          ) : (
            // Loading state placeholder button
            <div className="h-11 w-40 bg-slate-800 animate-pulse rounded-xl mx-auto"></div>
          )}
          
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="border-slate-800 bg-slate-900/50 hover:bg-slate-800/80 text-slate-300 font-medium px-6 py-2.5 rounded-xl transition-all duration-200 flex items-center justify-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </Button>
        </div>
      </div>
      
      {/* Footer copyright */}
      <div className="absolute bottom-6 left-0 right-0 text-center text-xs text-slate-600">
        © {new Date().getFullYear()} ImobPanda. Todos os direitos reservados.
      </div>
    </div>
  );
};
